import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { IBookingDTO } from '@shared/common';

/**
 * Client for appointment bookings.
 *
 * Tenant-facing reads route through the lobby service dashboard surface (`dashboard/:tenantID/bookings`).
 */
export class BookingClient {
  /** Singleton instance. */
  private static instance: BookingClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton BookingClient instance. */
  public static GetInstance(): BookingClient {
    return BookingClient.instance ?? (BookingClient.instance = new BookingClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/bookings - List a tenant's bookings, newest first, with optional free-text search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (customer/phone/notes/status).
   * @returns The tenant's bookings page, most recent first.
   */
  public async ListBookings(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<IBookingDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/bookings?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch bookings');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/bookings/count - Count a tenant's bookings matching an optional free-text search term.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param pattern - Optional free-text search term.
   * @returns The number of matching bookings.
   */
  public async CountBookings(signal: AbortSignal, tenantID: string, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/bookings/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch booking count');
    return await res.json();
  }
}
